import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isLuxPlusSubscriptionItem,
  isTourV2ExperienceItem,
  isSubscriptionJoinItem,
  isTourV2Item,
  isNoProtectionInsuranceItem,
  isInsuranceItem, isBedbankItem,
  isExperienceTicketViewItem,
  isExperienceItem,
  isLEHotelItem,
  isLoyaltyTransferItem,
  isLoyaltyLoungePassItem,
  isFlightItem,
} from 'lib/checkout/checkoutUtils'
import { calculateTotalJourneyBaggageCost } from 'checkout/lib/utils/flights/extras'
import { subDays } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { sum } from 'lib/array/arrayUtils'
import { getInsuranceTotals } from '../insurance'
import getAllItemsDiscountTotals from '../getAllItemsDiscountTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import { getAllOffers } from 'selectors/offerSelectors'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { AGENT_HUB_PAYMENT_MODE, PAYMENT_SCHEDULE_TYPE } from 'constants/payment'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import isPaymentScheduleAvailable from './isPaymentScheduleAvailable'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { getAccommodationBreakdownView } from 'checkout/selectors/view/accommodation'
import { getFlashPaymentScheduleTemplateOverride } from './flashPaymentScheduleExperiment'

function mapItemType(item: App.Checkout.AnyItem): string {
  if (isTourV2Item(item)) {
    return 'tour_v2'
  }
  if (isTourV2ExperienceItem(item)) {
    return 'tour_optional_experience'
  }
  if (isBedbankItem(item)) {
    return 'bedbank'
  }
  if (isLEHotelItem(item)) {
    return 'accommodation'
  }
  if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
    return 'subscription'
  }
  if (isExperienceTicketViewItem(item) || isLoyaltyLoungePassItem(item)) {
    return 'experience'
  }
  if (isLoyaltyTransferItem(item)) {
    return 'transfer'
  }
  if (isFlightItem(item)) {
    return 'flight'
  }
  return item.itemType
}

function mapOfferType(item: App.Checkout.AnyItem, offers: Record<string, App.AnyOffer>): string | undefined {
  if (!isLEHotelItem(item)) {
    return undefined
  }
  const offer = offers[item.offerId]
  return offer?.type
}

function getScheduleData(
  paymentScheduleInfo: App.PaymentScheduleInfo | undefined,
  scheduleType: PAYMENT_SCHEDULE_TYPE,
): { templateId: string | undefined; supplierPaymentNumberOfDays: number | undefined } | undefined {
  if (!paymentScheduleInfo) {
    return undefined
  }

  const schedule = scheduleType === PAYMENT_SCHEDULE_TYPE.STATIC
    ? paymentScheduleInfo.staticSchedule
    : paymentScheduleInfo.dynamicSchedule

  return schedule
}

const createPaymentScheduleSelector = (scheduleType: PAYMENT_SCHEDULE_TYPE) => createSelector(
  (state: App.State) => isPaymentScheduleAvailable(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => state.checkout.commissionData?.itemsCommission,
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => state.checkout.payment.useGiftCard,
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => getFlashPaymentScheduleTemplateOverride(state),
  (
    isPaymentScheduleAvailable,
    insuranceTotals,
    discountTotalToCartItemsMap,
    checkoutWithMemberPrice,
    cartItems,
    offers,
    itemViews,
    experienceItemViews,
    accommodationBreakdownView,
    itemsCommission,
    agentHubPaymentMode,
    bedbankOfferRates,
    currency,
    isCreditApplied,
    isGiftCardApplied,
    redeemedGiftCards,
    flashPaymentScheduleTemplateOverride,
  ): App.Checkout.PaymentScheduleRequestInfo | null => {
    if (!isPaymentScheduleAvailable) {
      return null
    }

    const giftCardAmount = sum(redeemedGiftCards.map((giftCard: { amount: number }) => giftCard.amount))

    const shouldUseAgentHubCommission = isAgentHubEnabled && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_NET_AMOUNT

    const getPaymentScheduleInfoFromOffer = (item: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem): App.PaymentScheduleInfo | undefined => {
      if (isTourV2Item(item)) {
        const tourV2Offer = offers[getTourV2IdForCheckoutItem(item)] as App.Tours.TourV2Offer
        return tourV2Offer?.paymentScheduleInfo
      } else if (isBedbankItem(item)) {
        const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
        const bedbankRates = bedbankOfferRates[item.offerId]?.[rateKey]
        const rate = bedbankRates?.find(rate => rate.id === item.roomRateId)
        return rate?.paymentScheduleInfo
      } else if (isLEHotelItem(item)) {
        const hotelOffer = offers[item.offerId] as App.HotelOffer
        const nativePaymentScheduleInfo = hotelOffer?.paymentScheduleInfo

        // When flashPaymentScheduleTemplateOverride is set, override the payment schedule info
        // to disable static schedules and enable dynamic schedules with the override template
        if (flashPaymentScheduleTemplateOverride) {
          return {
            staticSchedule: undefined,
            dynamicSchedule: {
              templateId: flashPaymentScheduleTemplateOverride,
              supplierPaymentNumberOfDays: nativePaymentScheduleInfo?.dynamicSchedule?.supplierPaymentNumberOfDays ?? 45,
            },
          }
        }

        return nativePaymentScheduleInfo
      }
    }

    const getStartDateFromItem = (item: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem): string | undefined => {
      if (isTourV2Item(item)) {
        return item.startDate
      } else if (isBedbankItem(item)) {
        return 'checkIn' in item ? item.checkIn : undefined
      } else if (isLEHotelItem(item)) {
        return 'checkIn' in item ? item.checkIn : undefined
      }
      return undefined
    }

    // Define item type configurations with priority order
    const itemTypeConfigs = [
      {
        type: 'tour',
        items: cartItems.filter(isTourV2Item),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
      {
        type: 'bedbank',
        items: cartItems.filter(isBedbankItem),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
      {
        type: 'hotel',
        items: cartItems.filter(isLEHotelItem),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
    ]

    // Find the first item with a template ID across all item types
    let itemWithTemplateId: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem | undefined

    for (const config of itemTypeConfigs) {
      itemWithTemplateId = config.items.find(config.hasPaymentScheduleInfo)
      if (itemWithTemplateId) {
        break
      }
    }

    if (!itemWithTemplateId) {
      return null
    }

    const paymentScheduleInfo = getPaymentScheduleInfoFromOffer(itemWithTemplateId)
    const schedule = getScheduleData(paymentScheduleInfo, scheduleType)

    if (!schedule?.templateId) {
      return null
    }

    if (!schedule.supplierPaymentNumberOfDays) {
      return null
    }
    // For bedbank items, we need to get the total from the item view
    if (isBedbankItem(itemWithTemplateId)) {
      const bedbankItemView = itemViews.data.get(itemWithTemplateId.itemId)?.data as App.Checkout.BedbankAccommodationItemView | undefined
      if (!bedbankItemView?.totals?.price) {
        return null
      }
    } else if (isLEHotelItem(itemWithTemplateId)) {
      const hotelItemView = itemViews.data.get(itemWithTemplateId.itemId)?.data as App.Checkout.AccommodationItemView | undefined
      if (!hotelItemView?.totals?.price) {
        return null
      }
    } else if (!itemWithTemplateId.total) {
      return null
    }

    const startDate = getStartDateFromItem(itemWithTemplateId)

    if (!startDate) {
      return null
    }

    const dueDate = subDays(new Date(startDate), schedule.supplierPaymentNumberOfDays)

    const items = cartItems
      .filter(item => !(isSubscriptionJoinItem(item) && item.waived))
      .filter(item => !isNoProtectionInsuranceItem(item))
      .map((item) => {
        let total: number | undefined = undefined
        let discountTotal = discountTotalToCartItemsMap.has(item.itemId) ? discountTotalToCartItemsMap.get(item.itemId) : 0
        if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
          total = item.amount ?? undefined
        } else if (isInsuranceItem(item)) {
          total = insuranceTotals.hasRequiredData ? insuranceTotals.data.price : 0
        } else if (isBedbankItem(item)) {
          const bedbankItemView = itemViews.data.get(item.itemId)?.data as App.Checkout.BedbankAccommodationItemView | undefined
          total = (checkoutWithMemberPrice && bedbankItemView?.totals?.memberPrice) ? bedbankItemView?.totals?.memberPrice : bedbankItemView?.totals?.price ?? undefined
        } else if (isExperienceItem(item)) {
          const itemView = experienceItemViews.data.find(view => view.id === item.itemId)
          // Use the calculated total from the experience item view which properly accounts for quantity
          total = (checkoutWithMemberPrice && itemView?.totals?.memberPrice) ? itemView.totals.memberPrice : itemView?.totals?.price ?? undefined
          // discounts are stored at the ticket level for experiences, so we have to manually get it from the ticket views
          const discountTotals = itemView?.ticketViews?.map((ticketView) => discountTotalToCartItemsMap.get(ticketView.item.itemId)) ?? []
          if (discountTotals.every(total => total !== undefined)) {
            discountTotal = sum(discountTotals)
          }
        } else if (isLEHotelItem(item)) {
          const hotelBreakdownView = accommodationBreakdownView.data.accommodations.find(view => view.items.some(itemView => itemView.itemId === item.itemId))
          const hotelItemView = hotelBreakdownView?.items.find(itemView => itemView.itemId === item.itemId)
          total = (checkoutWithMemberPrice && hotelItemView?.memberPrice) ? hotelItemView?.memberPrice : hotelItemView?.price ?? undefined
          total = total ? (total + (hotelItemView?.taxesAndFees ?? 0)) : undefined
        } else if (isLoyaltyTransferItem(item) || isLoyaltyLoungePassItem(item)) {
          // since it is a loyalty item, sometimes total is not present, so we set it to 0
          total = item.total ?? 0
        } else if (isFlightItem(item)) {
          // For flight items, calculate total from all flights
          // The isPaymentScheduleSupported flag will determine if it goes into schedule or upfront
          const flightItemView = itemViews.data.get(item.itemId)?.data as App.Checkout.FlightItemView | undefined
          // Baggage total must be calculated from the item view as it has journey data with baggage pricing
          // The cart item's otherFees does not have flightBaggageTotal populated
          const baggageTotal = flightItemView ? calculateTotalJourneyBaggageCost(flightItemView) : 0
          const flightFees = (item.otherFees?.flightSelectedSeatsTotal ?? 0) + baggageTotal + (item.otherFees?.flightBookingFee ?? 0) + (item.otherFees?.flightCreditFee ?? 0) + (item.otherFees?.flightServiceFee ?? 0)
          total = sum(
            item.flights.map(flight => flight.cost ?? 0),
          ) + flightFees
        } else {
          total = (checkoutWithMemberPrice && item.memberTotal) ? item.memberTotal : item.total
        }

        const commission = shouldUseAgentHubCommission ? itemsCommission?.[getAgentHubCommissionItemKey(item)]?.itemDiscount : 0

        // Check if any flight has paymentScheduleSupported for flight items
        const isPaymentScheduleSupported = isFlightItem(item) && item.flights.some(flight => flight.paymentScheduleSupported === true)

        return {
          idItems: item.itemId,
          total,
          type: mapItemType(item),
          discountTotal: shouldUseAgentHubCommission && commission ? commission : discountTotal,
          offerType: mapOfferType(item, offers),
          ...(isPaymentScheduleSupported && { isPaymentScheduleSupported: true }),
        }
      })

    // We don't want to call payment schedule endpoint if any item has an undefined total or discountTotal
    if (!items.every((item): item is typeof item & { total: number, discountTotal: number } => item.total !== undefined && item.discountTotal !== undefined)) {
      return null
    }

    return {
      templateId: schedule.templateId,
      bookingInfo: {
        bookingDate: moment().format(ISO_DATE_FORMAT),
        checkinDate: startDate,
        supplierDueDate: moment(dueDate).format(ISO_DATE_FORMAT),
        isCreditApplied,
        isGiftCardApplied,
        giftCardAmount,
        currency,
        items,
      },
    }
  },
)

export default createPaymentScheduleSelector
